// AI FORMAT:
// <path>File path</path>
// <content>File content</content>

// SANDPACK FORMAT:
// {
//     'filepath': 'file content'
// }

// GITHUB FORMAT:
// [
//     {
//         path: 'filepath',
//         content: 'file content'
//     }
// ]

// AIDIFF FORMAT:
// filepath
// ```language
// <<<<<<< SEARCH
// search content
// =======
// replace content
// >>>>>>> REPLACE
// ```

// ALT AIDIFF FORMAT:
// ```language:filepath
// <<<<<<< SEARCH
// search content
// =======
// replace content
// >>>>>>> REPLACE
// ```

export enum CodeFormat {
  AI = 'AI',
  SANDPACK = 'SANDPACK',
  GITHUB = 'GITHUB',
  AIDIFF = 'AIDIFF'
}

export interface CodeFile {
    path: string;
    content: string;
}

export interface DiffFile {
    path: string;
    language?: string;
    content: string;
}

export type FileList = string[];

export type AIFormat = string;
export type SandpackFormat = Record<string, string>;
export type GithubFormat = Array<CodeFile>;
export type AIDiffFormat = string;

export function formatCode(code: AIFormat | SandpackFormat | GithubFormat | AIDiffFormat, inputFormat: CodeFormat, outputFormat: CodeFormat): 
AIFormat | SandpackFormat | GithubFormat | AIDiffFormat {
  // If input and output formats are the same, return the code as is
  if (inputFormat === outputFormat) {
    return code;
  }
  
  // First parse the input into a common format
  const files: CodeFile[] = parseInput(code, inputFormat);
  
  // Then convert to the desired output format
  const formattedCode = formatOutput(files, outputFormat);
  
  // console.log('Formatted code from', JSON.stringify(inputFormat), 'to', JSON.stringify(outputFormat));
  
  return formattedCode;
}

// Parses input code into a common format
function parseInput(code: AIFormat | SandpackFormat | GithubFormat | AIDiffFormat, format: CodeFormat): CodeFile[] {
  switch (format) {
    case CodeFormat.AI:
      return parseAIFormat(code as AIFormat);
    case CodeFormat.SANDPACK:
      return parseSandpackFormat(code as SandpackFormat);
    case CodeFormat.GITHUB:
      return parseGithubFormat(code as GithubFormat);
    case CodeFormat.AIDIFF:
      return parseAIDiffFormat(code as AIDiffFormat);
    default:
      throw new Error(`Unsupported input format: ${format}`);
  }
}

// Formats files array into the desired output format
function formatOutput(files: CodeFile[], format: CodeFormat): 
AIFormat | SandpackFormat | GithubFormat {
  switch (format) {
    case CodeFormat.AI:
      return formatAI(files);
    case CodeFormat.SANDPACK:
      return formatSandpack(files);
    case CodeFormat.GITHUB:
      return formatGithub(files);
    default:
      throw new Error(`Unsupported output format: ${format}`);
  }
}

export function parseAIFormat(codeRaw: AIFormat): CodeFile[] {

  const code = codeRaw;
  //const code = codeRaw.replace(/\\n/g, "\n");

  const fileMap = new Map<string, CodeFile>();  // Changed to Map
  let regex = /<path>(.*?)<\/path>\s*<content>([\s\S]*?)(?=<\/?(?:content|path)>)/g;
  let match;

  // console.log("parse code", code);

  while ((match = regex.exec(code)) !== null) {
    // Overwrite existing entries by using Map.set()

    // console.log("code parsed", match[1], match[2]);

    fileMap.set(match[1], {
      path: match[1],
      content: match[2]
    });
  }

  return Array.from(fileMap.values());  // Convert back to array
}

function parseSandpackFormat(code: SandpackFormat): 
CodeFile[] {
  const files: CodeFile[] = [];
  
  for (const [path, content] of Object.entries(code)) {
    files.push({
      path,
      content: content
    });
  }
  
  return files;
}

function parseGithubFormat(code: GithubFormat): 
CodeFile[] {
  // Github format is already in the correct structure, just return as is
  return code;
}

// Parse AIDiff format into CodeFile array
function parseAIDiffFormat(code: AIDiffFormat): CodeFile[] {
  const fileMap = new Map<string, CodeFile>();  // Use a map to handle duplicate file paths
  
  // This regex now tries to capture either:
  // 1. A line followed by ```language
  // 2. ```language:filepath
  const regex = /(?:^(.*?)\n)?```([a-zA-Z]*)(?::(.*?))?\n([\s\S]*?)```/gm;
  let match;
  
  while ((match = regex.exec(code)) !== null) {
    // Group 1: filePath for AIDIFF_FORMAT (e.g., "filepath")
    // Group 2: language (e.g., "language" or "language:filepath")
    // Group 3: filePath for ALT_AIDIFF_FORMAT (e.g., "filepath" from "```language:filepath")
    // Group 4: diffContent
    
    let filePath = '';
    const language = match[2];
    const diffContent = match[4]; // The actual diff content
    
    // Determine the correct filePath based on which format matched
    if (match[3]) { // If Group 3 exists, it's ALT_AIDIFF_FORMAT
      filePath = match[3].trim();
    } else if (match[1]) { // If Group 1 exists, it's AIDIFF_FORMAT
      filePath = match[1].trim();
    } else {
      // If neither path group is present, skip this match
      continue;
    }
    
    if (!filePath || !diffContent) continue;
    
    // Check if the content has the fence format
    if (diffContent.includes('<<<<<<< SEARCH') && 
        diffContent.includes('=======') && 
        diffContent.includes('>>>>>>> REPLACE')) {
      
      // Process the diff content to extract the replace part for new files
      const searchReplaceRegex = /<<<<<<< SEARCH\s*([\s\S]*?)\s*=======\s*([\s\S]*?)\s*>>>>>>> REPLACE/g;
      const srMatches = diffContent.match(searchReplaceRegex);
      
      // Reset the regex for use in the loop
      searchReplaceRegex.lastIndex = 0;
      
      let srMatch;
      let processedContent = diffContent;
      let hasEmptySearch = false;
      
      // Process all search/replace blocks in the file
      while ((srMatch = searchReplaceRegex.exec(diffContent)) !== null) {
        const searchText = srMatch[1].trim();
        const replaceText = srMatch[2].trim();
        
        // If this is a new file (empty search text), use the replace text directly
        if (searchText === '') {
          hasEmptySearch = true;
          processedContent = replaceText;
          break; // For new files, just use the replace content directly
        }
      }
      
      // After processing all blocks, add the file
      if (hasEmptySearch) {
        fileMap.set(filePath, {
          path: filePath,
          content: processedContent
        });
      } else {
        // For existing files, store the entire content including fences
        // If we already have content for this file, append the new diff content
        if (fileMap.has(filePath)) {
          const existingContent = fileMap.get(filePath)!.content;
          fileMap.set(filePath, {
            path: filePath,
            content: existingContent + '\n' + diffContent
          });
        } else {
          fileMap.set(filePath, {
            path: filePath,
            content: diffContent
          });
        }
      }
    } else {
      // This is a plain file without SEARCH/REPLACE markers - just use the content as is
      fileMap.set(filePath, {
        path: filePath,
        content: diffContent
      });
    }
  }
  
  return Array.from(fileMap.values());
}

// Format-specific output functions
function formatAI(files: CodeFile[]): 
AIFormat {
  return files
    .map(file => `<path>${file.path}</path>\n<content>${file.content}</content>`)
    .join('\n\n');
}

function formatSandpack(files: CodeFile[]): 
SandpackFormat {
  const output: Record<string, string> = {};
  files.forEach(file => {
    output[file.path] = file.content;
  });
  return output;
}

function formatGithub(files: CodeFile[]): 
GithubFormat {
  return files.map(file => ({
      path: file.path,
      content: file.content
    })
  );
}

export function combineCode(allCode: GithubFormat, newCode: GithubFormat): 
GithubFormat {
    const combinedMap = new Map<string, CodeFile>();

    // Add all files from allCode to the combined map
    allCode.forEach(file => {
        combinedMap.set(file.path, file);
    });

    // Overwrite files from newCode if they have the same path
    newCode.forEach(file => {
        combinedMap.set(file.path, file);
    });

// console.log('Combined code: ', combinedMap);
    // Convert the map back to an array
    return Array.from(combinedMap.values());
}

export function extractCodeForAI(code: GithubFormat, filesForAIList: FileList):
AIFormat {
  const extractedFiles: CodeFile[] = code.filter(file => filesForAIList.includes(file.path));
  const formattedFiles = formatAI(extractedFiles);
  
// console.log('Extracted code for AI:', formattedFiles);
  return formattedFiles;
}

const DISABLE_CONSOLE_LOGS = true;
const TEST_LOGS = "console.log('TEST LOG');\nconsole.warn('TEST WARNING');\n"
const DISABLE_CONSOLE_LOGS_PREFIX = "console.log = () => {};\n";

const CAPTURE_LOGS_URL = 'https://api.playmix.ai/storage/v1/object/public/playmix-assets/other/captureLogs.js';
const CAPTURE_LOGS_SCRIPT_TAG = `<script src="${CAPTURE_LOGS_URL}"></script>`;

const POSTHOG_INJECTION_SCRIPT = `
(function(t,e){var n=t.posthog=t.posthog||[];if(n.init)return;n.q=n.q||[];n.init=function(t,e){n.t=t;n.e=e};var i=e.createElement("script");i.type="text/javascript",i.async=!0,i.src="https://us-assets.i.posthog.com/static/array.js";var r=e.getElementsByTagName("script")[0];r.parentNode.insertBefore(i,r)})(window,document);
window.posthog.init('${process.env.REACT_APP_POSTHOG_API_KEY}', {
  api_host: '${process.env.REACT_APP_POSTHOG_HOST}',
  session_recording: { recordCrossOriginIframes: true }
});
`;

// Transform code files for proper formatting and functionality
export const localTransformCode = (codeItem: any, isMultipleFiles: boolean, template: string): any => {
  const replaceCdn = (content: string) => {
      return content.replace("https://cdn.jsdelivr.net/npm/pixi.js@7.4.2/+esm", "pixi.js")
        .replace("https://cdn.jsdelivr.net/npm/zustand@5.0.3/vanilla/+esm", "zustand/vanilla");
  };

  const fixPath = (path: string) => {
    let newPath = path.replace("//", "")
    newPath = newPath.startsWith("/") ? newPath.replace("/", "") : newPath;
    return newPath;
  }

  let content = codeItem.content;

  // Inject the script tag into HTML files before the closing head tag
  if (isMultipleFiles && codeItem.path.endsWith("index.html")) {
      if (content.includes('</head>') && !content.includes(CAPTURE_LOGS_SCRIPT_TAG)) {
          content = content.replace('</head>', `${CAPTURE_LOGS_SCRIPT_TAG}\n</head>`);
      }
  }

  // Inject PostHog & console log disable into the main entry point
  if (isMultipleFiles && (codeItem.path.endsWith("index.ts") || codeItem.path.endsWith("index.tsx"))) {
      if (!content.includes('posthog.init')) {
          content = POSTHOG_INJECTION_SCRIPT + "\n" + content;
      }
      
      if (DISABLE_CONSOLE_LOGS) {
          if (!content.includes(DISABLE_CONSOLE_LOGS_PREFIX)) {
              content = DISABLE_CONSOLE_LOGS_PREFIX + content;
          }
      }
      content = replaceCdn(content);
  } else {
      content = replaceCdn(content);
  }
  
  const transformedCodeItem = {
      ...codeItem,
      path: (isMultipleFiles && template === 'phaser-react' && codeItem.path.includes("html") && !codeItem.path.includes("public"))
          ? ("public/" + fixPath(codeItem.path))
          : fixPath(codeItem.path),
      content: content
  };

  return transformedCodeItem;
};

// Inverse of the local code transformation
export const localUntransformCode = (codeItem: { path: string, content: string }, isMultipleFiles: boolean): CodeFile | null => {
  if (!isMultipleFiles) {
    return codeItem;
  }

  const replaceCdn = (content: string) => {
    return content.replace(/pixi\.js(?!\S)/g, "https://cdn.jsdelivr.net/npm/pixi.js@7.3.3/+esm")
      .replace(/zustand\/vanilla(?!\S)/g, "https://cdn.jsdelivr.net/npm/zustand@5.0.3/vanilla/+esm");
  }

  // Delete files with blank content by returning null
  if (codeItem.content.trim() === '') {
    // console.log('Deleting code item with blank content: ', codeItem);
    return null;
  }

  let untransformedContent = codeItem.content;

  if (codeItem.path.endsWith("index.ts") || codeItem.path.endsWith("index.tsx")) {
    // Remove all prefixes during untransformation
    untransformedContent = untransformedContent.replace(POSTHOG_INJECTION_SCRIPT + "\n", "");
    untransformedContent = untransformedContent.replace(DISABLE_CONSOLE_LOGS_PREFIX, "");
    untransformedContent = replaceCdn(untransformedContent);
  } else {
    // Replace local module names with CDN URLs
    untransformedContent = replaceCdn(untransformedContent);

    // Remove the injected script tag from HTML files during untransformation
    if (codeItem.path.endsWith("index.html")) {
        untransformedContent = untransformedContent.replace(`${CAPTURE_LOGS_SCRIPT_TAG}\n`, "");
    }
  }
  
  return { ...codeItem, content: untransformedContent };
};